import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiService } from "../services/api";
import { CallAnalysis } from "../types/api";
import {
  ArrowLeftIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import jsPDF from "jspdf";

const CallDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [call, setCall] = useState<CallAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [managerNotes, setManagerNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  // Audio player
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const loadCallData = useCallback(async () => {
    try {
      const response = await apiService.getCall(id!);
      if (response.success) {
        setCall(response.data!);
        setManagerNotes(response.data!.manager_notes || "");
      } else {
        toast.error("Call not found");
        navigate("/team");
      }
    } catch (error) {
      console.error("Failed to load call data:", error);
      toast.error("Failed to load call data");
      navigate("/team");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      loadCallData();
    }
  }, [id, loadCallData]);

  const handleDeleteCall = async () => {
    try {
      const response = await apiService.deleteCall(id!);
      if (response.success) {
        toast.success("Call analysis deleted successfully");
        if (call?.salesperson_id) {
          navigate(`/salesperson/${call.salesperson_id}`);
        } else {
          navigate("/team");
        }
      } else {
        toast.error(response.message || "Failed to delete call");
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.message || "Failed to delete call");
    }
    setShowDeleteConfirm(false);
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const response = await apiService.updateManagerNotes(id!, managerNotes);
      if (response.success) {
        toast.success("Notes saved successfully");
        setEditingNotes(false);
        if (call) {
          setCall({ ...call, manager_notes: managerNotes });
        }
      } else {
        toast.error(response.message || "Failed to save notes");
      }
    } catch (error: any) {
      console.error("Save notes error:", error);
      toast.error(error.response?.data?.message || "Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleExportTranscript = () => {
    if (!call?.transcript) {
      toast.error("No transcript available");
      return;
    }

    const element = document.createElement("a");
    const file = new Blob([call.transcript], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `transcript-${call.audio_file_name || "call"}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleExportPdf = () => {
    if (!call) return;

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 48;
    const contentW = pageW - margin * 2;
    let y = margin;

    const checkPageBreak = (needed: number) => {
      if (y + needed > pageH - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const addSectionTitle = (text: string) => {
      checkPageBreak(40);
      y += 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(30, 30, 30);
      doc.text(text, margin, y);
      y += 7;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageW - margin, y);
      y += 18;
    };

    const addField = (label: string, value: string | null | undefined) => {
      if (!value) return;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      const labelText = `${label}: `;
      const labelW = doc.getTextWidth(labelText);
      const valueMaxW = contentW - labelW;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 30, 30);
      const valueLines = doc.splitTextToSize(value, valueMaxW);
      checkPageBreak(valueLines.length * 15 + 10);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(labelText, margin, y);

      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 30, 30);
      doc.text(valueLines, margin + labelW, y);
      y += valueLines.length * 15 + 6;
    };

    const addParagraph = (text: string) => {
      const lines = doc.splitTextToSize(text, contentW);
      lines.forEach((line: string) => {
        checkPageBreak(16);
        doc.text(line, margin, y);
        y += 16;
      });
      y += 8;
    };

    const addBulletList = (items: string[]) => {
      items.forEach((item) => {
        const lines = doc.splitTextToSize(`• ${item}`, contentW - 12);
        lines.forEach((line: string, i: number) => {
          checkPageBreak(16);
          doc.text(line, margin + (i === 0 ? 0 : 12), y);
          y += 15;
        });
      });
      y += 4;
    };

    // ── Header ──────────────────────────────────────────────────────────
    doc.setFillColor(17, 24, 39);
    doc.rect(0, 0, pageW, 72, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text("Call Analysis Report", margin, 38);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(180, 180, 180);
    const salesperson =
      call.salesperson_first_name && call.salesperson_last_name
        ? `${call.salesperson_first_name} ${call.salesperson_last_name}`
        : "Unknown Salesperson";
    doc.text(
      `${salesperson}  •  ${call.audio_file_name}  •  ${new Date(call.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`,
      margin,
      56
    );
    y = 96;

    // ── Overview ─────────────────────────────────────────────────────────
    addSectionTitle("Overview");
    addField("Status", call.status);
    addField("Overall Grade", call.average_grade != null ? String(call.average_grade) : "N/A");
    addField(
      "Outcome",
      call.objective_achieved == null
        ? "N/A"
        : call.objective_achieved
        ? "Objective achieved"
        : "Objective not achieved"
    );
    addField("File", call.audio_file_name);

    // ── AI Summary ───────────────────────────────────────────────────────
    if (call.ai_summary) {
      addSectionTitle("AI Summary");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      addParagraph(call.ai_summary);
    }

    // ── Performance Scores ───────────────────────────────────────────────
    if (call.scores && Object.keys(call.scores).length > 0) {
      addSectionTitle("Performance Metrics");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      Object.entries(call.scores).forEach(([key, score]) => {
        const label = key
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
        addField(label, `${score} / 10`);
      });
    }

    // ── Detailed Analysis ────────────────────────────────────────────────
    if (call.analysis) {
      if (call.analysis.strengths?.length > 0) {
        addSectionTitle("Strengths");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(30, 30, 30);
        addBulletList(call.analysis.strengths);
      }
      if (call.analysis.areas_for_improvement?.length > 0) {
        addSectionTitle("Areas for Improvement");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(30, 30, 30);
        addBulletList(call.analysis.areas_for_improvement);
      }
      if (call.analysis.red_flags?.length > 0) {
        addSectionTitle("Red Flags");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(30, 30, 30);
        addBulletList(call.analysis.red_flags);
      }
      if (call.analysis.talk_ratio) {
        addSectionTitle("Talk Ratio");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(30, 30, 30);
        addParagraph(call.analysis.talk_ratio);
      }
      if (call.analysis.next_steps_recommendation) {
        addSectionTitle("Next Steps Recommendation");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(30, 30, 30);
        addParagraph(call.analysis.next_steps_recommendation);
      }
    }

    // ── Manager Notes ────────────────────────────────────────────────────
    if (call.manager_notes) {
      addSectionTitle("Manager Notes");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      addParagraph(call.manager_notes);
    }

    // ── Transcript ───────────────────────────────────────────────────────
    if (call.transcript) {
      addSectionTitle("Transcript");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      addParagraph(call.transcript);
    }

    // ── Footer on every page ─────────────────────────────────────────────
    const totalPages = (doc.internal as any).getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.text(
        `Page ${i} of ${totalPages}  •  Generated ${new Date().toLocaleString()}`,
        margin,
        pageH - 24
      );
    }

    const safeName = (call.audio_file_name || "call").replace(/\.[^.]+$/, "");
    doc.save(`analysis-${safeName}.pdf`);
  };

  const handleLoadAudio = () => {
    const token = localStorage.getItem("token");
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    setAudioUrl(`${apiUrl}/calls/${id}/stream?token=${token}`);
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch((err) => {
        console.error("[Audio] play() rejected:", err?.name, err?.message);
      });
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = Number(e.target.value);
    setCurrentTime(t);
    if (audioRef.current) audioRef.current.currentTime = t;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    setVolume(v);
    if (audioRef.current) {
      audioRef.current.volume = v;
      setIsMuted(v === 0);
    }
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    const next = !isMuted;
    audioRef.current.muted = next;
    setIsMuted(next);
  };

  const formatTime = (s: number) => {
    if (!isFinite(s) || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-800 bg-green-100";
      case "processing":
      case "transcribing":
      case "analyzing":
        return "text-yellow-800 bg-yellow-100";
      case "failed":
        return "text-red-800 bg-red-100";
      default:
        return "text-gray-800 bg-gray-100";
    }
  };

  const getGradeColor = (grade?: number) => {
    if (!grade) return "text-gray-600";
    if (grade >= 8) return "text-green-600";
    if (grade >= 6) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return "bg-green-500";
    if (score >= 6) return "bg-yellow-500";
    return "bg-red-500";
  };

  const formatScoreLabel = (scoreKey: string) => {
    return scoreKey
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!call) {
    return (
      <div className="text-center">
        <p className="text-gray-500">Call not found</p>
        <Link to="/team" className="text-indigo-600 hover:text-indigo-500">
          Back to Team
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-gray-500"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Call Analysis
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {call.salesperson_first_name && call.salesperson_last_name
                ? `${call.salesperson_first_name} ${call.salesperson_last_name}`
                : "Unknown Salesperson"}{" "}
              • {call.audio_file_name}
            </p>
          </div>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={() => setShowTranscript(!showTranscript)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <EyeIcon className="h-4 w-4 mr-2" />
            {showTranscript ? "Hide" : "View"} Transcript
          </button>
          {call.transcript && (
            <button
              onClick={handleExportTranscript}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Transcript
            </button>
          )}
          {call.status === "completed" && (
            <button
              onClick={handleExportPdf}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              PDF Summary
            </button>
          )}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Audio Player */}
      {call.status === "completed" && (
        <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
          {!audioUrl ? (
            <div className="flex items-center gap-4">
              <button
                onClick={handleLoadAudio}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
              >
                <PlayIcon className="h-4 w-4" />
                Play Recording
              </button>
              <span className="text-sm text-gray-400 font-mono truncate max-w-xs">
                {call.audio_file_name}
              </span>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Hidden audio element */}
              <audio
                ref={audioRef}
                src={audioUrl!}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
                onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
                onCanPlayThrough={() => {
                  audioRef.current?.play().catch((err) => {
                    console.error("[Audio] play() rejected:", err?.name, err?.message);
                  });
                }}
                onError={() => {
                  const err = audioRef.current?.error;
                  console.error("[Audio] error — code:", err?.code, "src:", audioRef.current?.src);
                  toast.error("Audio failed to load");
                }}
                style={{ display: "none" }}
              />

              <div className="flex items-center gap-4">
                {/* Play/Pause */}
                <button
                  onClick={togglePlayPause}
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-gray-900 text-white hover:bg-gray-700 transition-colors"
                >
                  {isPlaying ? (
                    <PauseIcon className="h-4 w-4" />
                  ) : (
                    <PlayIcon className="h-4 w-4" />
                  )}
                </button>

                {/* Seek bar + times */}
                <span className="text-xs text-gray-400 font-mono w-10 text-right flex-shrink-0">
                  {formatTime(currentTime)}
                </span>
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  step={0.1}
                  value={currentTime}
                  onChange={handleSeek}
                  className="flex-1 h-1 rounded-full accent-gray-900 cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #111 ${duration ? (currentTime / duration) * 100 : 0}%, #e5e7eb ${duration ? (currentTime / duration) * 100 : 0}%)`,
                  }}
                />
                <span className="text-xs text-gray-400 font-mono w-10 flex-shrink-0">
                  {formatTime(duration)}
                </span>

                {/* Volume */}
                <button
                  onClick={toggleMute}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-700 transition-colors"
                >
                  {isMuted || volume === 0 ? (
                    <SpeakerXMarkIcon className="h-4 w-4" />
                  ) : (
                    <SpeakerWaveIcon className="h-4 w-4" />
                  )}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.02}
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 rounded-full accent-gray-900 cursor-pointer flex-shrink-0"
                  style={{
                    background: `linear-gradient(to right, #111 ${(isMuted ? 0 : volume) * 100}%, #e5e7eb ${(isMuted ? 0 : volume) * 100}%)`,
                  }}
                />
              </div>

              <p className="text-xs text-gray-400 font-mono truncate">
                {call.audio_file_name}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Status and Grade */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 mb-2">
                Status
              </div>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(call.status)}`}
              >
                {call.status}
              </span>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 mb-2">
                Overall Grade
              </div>
              {call.average_grade ? (
                <div
                  className={`text-3xl font-bold ${getGradeColor(call.average_grade)}`}
                >
                  {call.average_grade}
                </div>
              ) : (
                <div className="text-3xl font-bold text-gray-400">N/A</div>
              )}
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 mb-2">
                Outcome
              </div>
              <div className="flex items-center justify-center h-12">
                {(() => {
                  const objectiveAchieved = call.objective_achieved;
                  if (
                    objectiveAchieved === null ||
                    objectiveAchieved === undefined
                  ) {
                    return (
                      <span className="h-4 w-4 rounded-full bg-gray-400"></span>
                    );
                  }
                  return objectiveAchieved ? (
                    <span className="h-4 w-4 rounded-full bg-green-500"></span>
                  ) : (
                    <span className="h-4 w-4 rounded-full bg-red-500"></span>
                  );
                })()}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 mb-2">Date</div>
              <div className="text-lg text-gray-600">
                {new Date(call.created_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transcript */}
      {showTranscript && call.transcript && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Call Transcript
              </h3>
              <button
                onClick={handleExportTranscript}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Export
              </button>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                {call.transcript}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Scores Breakdown */}
      {call.scores && Object.keys(call.scores).length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Performance Metrics
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-4">
              {Object.entries(call.scores).map(([key, score]) => (
                <div key={key} className="flex items-center">
                  <div className="w-1/3">
                    <span className="text-sm font-medium text-gray-700">
                      {formatScoreLabel(key)}
                    </span>
                  </div>
                  <div className="w-2/3 flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className={`h-2 rounded-full ${getScoreColor(score)}`}
                        style={{ width: `${(score / 10) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 w-8">
                      {score}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI Summary */}
      {call.ai_summary && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              AI Summary
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <p className="text-gray-700 whitespace-pre-wrap">
              {call.ai_summary}
            </p>
          </div>
        </div>
      )}

      {/* Detailed Analysis */}
      {call.analysis && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium  text-gray-900">
              Detailed Analysis
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6 space-y-6">
            {call.analysis.strengths && call.analysis.strengths.length > 0 && (
              <div>
                <h4 className="text-md font-semibold text-green-800 mb-2">
                  Strengths
                </h4>
                <ul className="list-disc list-inside space-y-1">
                  {call.analysis.strengths.map(
                    (strength: string, index: number) => (
                      <li key={index} className="text-gray-700">
                        {strength}
                      </li>
                    ),
                  )}
                </ul>
              </div>
            )}

            {call.analysis.areas_for_improvement &&
              call.analysis.areas_for_improvement.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-yellow-800 mb-2">
                    Areas for Improvement
                  </h4>
                  <ul className="list-disc list-inside space-y-1">
                    {call.analysis.areas_for_improvement.map(
                      (area: string, index: number) => (
                        <li key={index} className="text-gray-700">
                          {area}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}

            {call.analysis.red_flags && call.analysis.red_flags.length > 0 && (
              <div>
                <h4 className="text-md font-semibold text-red-800 mb-2">
                  Red Flags
                </h4>
                <ul className="list-disc list-inside space-y-1">
                  {call.analysis.red_flags.map(
                    (flag: string, index: number) => (
                      <li key={index} className="text-gray-700">
                        {flag}
                      </li>
                    ),
                  )}
                </ul>
              </div>
            )}

            {call.analysis.talk_ratio && (
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-2">
                  Talk Ratio
                </h4>
                <p className="text-gray-700">{call.analysis.talk_ratio}</p>
              </div>
            )}

            {call.analysis.next_steps_recommendation && (
              <div>
                <h4 className="text-md font-semibold text-blue-800 mb-2">
                  Next Steps Recommendation
                </h4>
                <p className="text-gray-700">
                  {call.analysis.next_steps_recommendation}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manager Notes */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Manager Notes
            </h3>
            <button
              onClick={() => {
                if (editingNotes) {
                  setManagerNotes(call.manager_notes || "");
                  setEditingNotes(false);
                } else {
                  setEditingNotes(true);
                }
              }}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={savingNotes}
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              {editingNotes ? "Cancel" : "Edit"}
            </button>
          </div>
        </div>
        <div className="px-4 py-5 sm:p-6">
          {editingNotes ? (
            <div className="space-y-4">
              <textarea
                value={managerNotes}
                onChange={(e) => setManagerNotes(e.target.value)}
                rows={6}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Add your thoughts about this call..."
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setManagerNotes(call.manager_notes || "");
                    setEditingNotes(false);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  disabled={savingNotes}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingNotes ? "Saving..." : "Save Notes"}
                </button>
              </div>
            </div>
          ) : (
            <div>
              {call.manager_notes ? (
                <p className="text-gray-700 whitespace-pre-wrap">
                  {call.manager_notes}
                </p>
              ) : (
                <p className="text-gray-500 italic">
                  No manager notes added yet.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">
                Delete Call Analysis
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this call analysis? This
                  action cannot be undone.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={handleDeleteCall}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300"
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md w-24 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallDetail;
